// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";
import merge from "lodash/merge";

export interface XCheckoutStateType {
    shipWhenComplete?: boolean;
    notes?: string;
    requestedPickupDate?: Date | null;
    useOneTimeAddress?: boolean;
    isUpdatingOneTimeAddress?: boolean;
    smid?: string;
    phone2?: string;
    companyDepartmentNumber?: string;
}

export const initialCheckoutState: XCheckoutStateType = {
    shipWhenComplete: false,
    notes: "",
    requestedPickupDate: null,
    useOneTimeAddress: false,
    isUpdatingOneTimeAddress: false,
    smid: "",
    phone2: "",
    companyDepartmentNumber: "",
};

const reducer = {
    // we need to call reset after submitting order
    "xCheckout/Reset": (draft: Draft<XCheckoutStateType>) => {
        draft["shipWhenComplete"] = false;
        draft["useOneTimeAddress"] = false;
        draft["isUpdatingOneTimeAddress"] = false;
        draft["notes"] = "";
        draft["requestedPickupDate"] = null;
        draft["smid"] = "";
        draft["phone2"] = "";
        draft["companyDepartmentNumber"] = "";
    },
    "xCheckout/SetNotes": (draft: Draft<XCheckoutStateType>, actions: { notes: string }) => {
        draft.notes = actions.notes;
    },
    "xCheckout/SetShipWhenComplete": (draft: Draft<XCheckoutStateType>, actions: { shipWhenComplete: boolean }) => {
        draft.shipWhenComplete = actions.shipWhenComplete;
    },
    "xCheckout/SetRequestedPickupDate": (draft: Draft<XCheckoutStateType>, actions: { requestedPickupDate: Date }) => {
        draft.requestedPickupDate = actions.requestedPickupDate;
    },
    "xCheckout/Update": (draft: Draft<XCheckoutStateType>, action: { data: Partial<XCheckoutStateType> }) => {
        draft = merge(draft, action.data);
    },
    "xCheckout/SetIsUpdatingOneTimeAddress": (
        draft: Draft<XCheckoutStateType>,
        actions: { isUpdatingOneTimeAddress: boolean },
    ) => {
        draft.isUpdatingOneTimeAddress = actions.isUpdatingOneTimeAddress;
    },
    "xCheckout/SetOneTimeAddressExtraFields": (
        draft: Draft<XCheckoutStateType>,
        actions: { smid: string; phone2: string; companyDepartmentNumber: string },
    ) => {
        draft["smid"] = actions.smid;
        draft["phone2"] = actions.phone2;
        draft["companyDepartmentNumber"] = actions.companyDepartmentNumber;
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
